import { useAppDispatch, useAppState } from '@/context/appState';
import { AuthUser, IShopifyAddress } from '@/context/types';
import { DefaultAddressBody, LoginBody, UserAPI } from '@/helpers/api/UserAPI';
import { parseDefaultAddress } from '@/helpers/misc';
import { useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// ============================================================================
// COMMERCIAL PATTERN: Global Auth Locks
// Declared OUTSIDE the hook so they instantly sync across all components
// ============================================================================
let isAuthLocked = false;
let authAbortController = new AbortController();

// 1. Name the function for React DevTools visibility
export default function useUser() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppState();

  const checkSession = useCallback(async (): Promise<boolean> => {
    // 1. Synchronous Block: Prevent firing if a logout is in progress
    if (isAuthLocked) return false;

    try {
      // 2. Attach the Abort Signal to your API call
      // Note: Ensure your UserAPI.get wrapper accepts an Axios/Fetch signal config!
      const res = await UserAPI.get({ signal: authAbortController.signal });

      // 3. Secondary Block: In case the lock was flipped while waiting for the network
      if (isAuthLocked) return false;

      if (res?.isLoggedIn && res?.user) {
        const curUser = res.user;

        dispatch({
          type: 'HYDRATE_USER',
          user: {
            ...res.user,
            defaultAddress: parseDefaultAddress(
              res.user?.defaultAddress as any,
            ) as AuthUser['defaultAddress'],
          },
        });
        return true;
      }

      dispatch({ type: 'CLEAR_USER' });
      return false;
    } catch (error: any) {
      // 4. Gracefully ignore intentional aborts so we don't throw console errors during logout
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('[AUTH]: Session check aborted due to logout/navigation.');
        return false;
      }

      console.error('[AUTH_ERROR]: Failed to verify session.', error);
      dispatch({ type: 'CLEAR_USER' });
      return false;
    }
  }, [dispatch]);

  const login = useCallback(
    async (body: LoginBody) => {
      try {
        const res = await UserAPI.post(body);

        if (res?.success && res?.user) {
          // 5. Unlock auth state upon successful login and reset the controller
          isAuthLocked = false;
          authAbortController = new AbortController();

          dispatch({
            type: 'HYDRATE_USER',
            user: {
              ...res.user,
              defaultAddress: parseDefaultAddress(
                res.user?.defaultAddress as any,
              ) as AuthUser['defaultAddress'],
            } as AuthUser,
          });
          return { isLoggedIn: true };
        }

        return { isLoggedIn: false };
      } catch (error) {
        console.error('[AUTH_ERROR]: Login failed.', error);
        throw error;
      }
    },
    [dispatch],
  );

  const update = useCallback(
    async (body: DefaultAddressBody) => {
      try {
        // This POST now returns { success: true, user: {...} }
        const res = await UserAPI.patch({ ...body, id: user!.id });

        if (res?.success && res?.user) {
          // Hydrate directly from the POST response! No GET needed.
          dispatch({
            type: 'HYDRATE_USER',
            user: {
              ...res.user,
              defaultAddress: parseDefaultAddress(
                res.user?.defaultAddress as any,
              ) as AuthUser['defaultAddress'],
            } as AuthUser,
          });
          return { success: true };
        }

        return { success: false };
      } catch (error) {
        console.error('[AUTH_ERROR]: Failed to update default address.', error);
        throw error;
      }
    },
    [dispatch],
  );

  const logout = useCallback(async () => {
    // 6. Instantly engage the lock to prevent overlapping checkSession calls
    isAuthLocked = true;

    // 7. Instantly kill any currently running checkSession network requests
    authAbortController.abort();

    try {
      const res = await UserAPI.delete();
      navigate('/', { replace: true });
      if (res?.success) {
        dispatch({ type: 'CLEAR_USER' });
      }
    } catch (error) {
      console.error('[AUTH_ERROR]: Logout failed.', error);
      // Optional: Unlock if logout fails so they aren't trapped in a broken state
      isAuthLocked = false;
      authAbortController = new AbortController();
      throw error;
    }
  }, [dispatch]);

  return { login, checkSession, logout, user, update };
}
